import { CellStyle } from 'ag-grid-community';

// How an unsaved cell is marked in the Budget grids (CALL-779): the WHOLE cell is washed in a translucent
// orange, over whatever cue it already carries. Nothing is drawn at an edge or a corner, and that is the
// conclusion of the versions before it — each was rejected on sight for the same reason:
//
//   - `2px solid #f44336` BORDER (inherited from Strategic Overview) read as a wall of validation errors.
//     A border joins the box, so it nudged the number sideways, and since every ring paints the edge it
//     shares with its neighbour, a run of edited months came out as a red lattice.
//   - A 1px inset OUTLINE fixed the shift and halved the weight, but two rings still meet on the shared
//     edge, so the lattice survived.
//   - A CORNER mark has no shared edge to double, but these columns are ~200px wide with the value on the
//     right: a top-left flag lands just past the PREVIOUS cell's number and reads as a stray tick stuck to
//     it, while a top-right one sits on top of its own digits. There is no free corner to use.
//
// A wash has no geometry to misread — no edge to double, nothing to sit beside the wrong number — and it
// keeps working in the metadata columns, where the text is left-aligned instead.
//
// It has to be an IMAGE, not a colour: backgroundColor carries the yellow/light-blue semantics (editable
// vs extrapolated) and would have to be recomputed per cue, while a translucent image layers over
// whichever of them the cell already has. `boxShadow` was unavailable too — ForecastVolumeGrid sets the
// manual-override accent's shadow AFTER spreading this style, so it would win. One property also keeps the
// clean-state reset below down to one key.
//
// The hue is the same #ed6c02 as that manual-override accent (CALL-753): one "needs your attention" colour
// in these grids rather than one per cue, and not the red that made this look like validation.
const MARK_COLOR = 'rgba(237, 108, 2, 0.2)'; // #ed6c02 at 20% — legible over both cream and pale blue

/** The wash itself, exported so CellLegend's swatch is painted by the same value the cells are. */
export const PENDING_WASH = `linear-gradient(${MARK_COLOR}, ${MARK_COLOR})`;

// The other cues the Budget grids paint, gathered here for the same reason: a legend that hardcoded its
// own copies would drift from the grids the first time one of them changed. The backgrounds are still
// written literally in the grids that only use one of them; ForecastVolumeGrid, which paints all three,
// reads them from here.
export const EDITABLE_CELL_BG = '#fffde7';
export const EXTRAPOLATED_CELL_BG = '#e3f2fd';
export const MANUAL_ACCENT_SHADOW = 'inset 3px 0 0 0 #ed6c02';
// CALL-820: the orphaned row's wash (working unit without active strategic volumes) — MUI red 50,
// the error family of the LinkOff icon that accompanies it, painted on the row so the editable
// cream cells keep their own meaning on top.
export const ORPHANED_ROW_BG = '#ffebee';
// CALL-822: an entered monthly Vertrags-ABT that differs from the row's Standard-ABT silently wins in
// the cost calculation, so the cell announces it. MUI purple 50 — the other cell hues (editable cream,
// extrapolated pale blue, the gap tint's orange 50) all read as states of the value itself; the
// override is about the value's RELATION to another number, so it gets a family of its own.
export const ABT_OVERRIDE_CELL_BG = '#f3e5f5';
// CALL-818: a cell whose absence causes the row's warning (ConfigDetailDialog) — MUI orange 50.
export const MISSING_CELL_BG = '#fff3e0';
// CALL-824: the greyed default shown in an empty price/ABT cell of the detail dialog.
export const DEFAULT_VALUE_TEXT = '#757575';

const MODIFIED_CELL_MARK: CellStyle = {
    backgroundImage: PENDING_WASH,
};

// ...and a CLEAN cell has to say so explicitly. ag-grid applies a style object additively (one
// `setProperty` per key) and these grids reuse cell elements across renders via getRowId, so a clean
// branch that merely omits the key leaves the mark painted — after a save, after Verwerfen, and on
// whatever row later reuses that element. BudgetSummary documents the same trap for backgroundColor.
//
// An empty string removes the inline property, so the cell's own background shows through again. The
// earlier border/outline are reset with it: a dev hot-reloading onto this change would otherwise keep
// a ring that nothing repaints.
const UNMODIFIED_CELL_MARK: CellStyle = {
    backgroundImage: '',
    border: '',
    outline: '',
};

/** The marker half of a Budget grid's cellStyle. Spread it in EVERY branch — both states are explicit. */
export const pendingBorder = (isModified: boolean): CellStyle =>
    isModified ? MODIFIED_CELL_MARK : UNMODIFIED_CELL_MARK;
